import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  TablePagination,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  MonetizationOn,
  Replay,
  Discount,
  AttachMoney,
} from "@mui/icons-material";
import { getSalesRevenueReport } from "../services/reportService";

const SalesRevenueReport: React.FC = () => {
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<"daily" | "weekly" | "monthly">("daily");
  const [washType, setWashType] = useState<string>("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const fetchReport = async () => {
    try {
      setLoading(true);

      const res = await getSalesRevenueReport(filterType, washType, page, limit);
      console.log("API RAW RESPONSE:", res);

      // ✅ Validate response
      if (res.status !== "success" || !res.data) {
        throw new Error("Invalid API response");
      }

      setReport(res.data);
      setError(null);

    } catch (err) {
      console.error("Error fetching Sales & Revenue Report:", err);
      setError("Failed to load report.");

      // Prevent table crash
      setReport({
        summary: {},
        listing: { rows: [], total_count: 0 },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [filterType, washType, page, limit]);

  if (loading && !report) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const summary = report?.summary || {};

  // Summary Card Configuration
  const summaryCards = [
    {
      title: "Total Revenue",
      value: `${process.env.REACT_APP_SITE_CURRENCY}${summary.total_revenue || 0}`,
      icon: <MonetizationOn sx={{ fontSize: 38, color: "#2e7d32" }} />,
    },
    {
      title: "Total Refunds",
      value: `${process.env.REACT_APP_SITE_CURRENCY}${summary.total_refund || 0}`,
      icon: <Replay sx={{ fontSize: 38, color: "#c62828" }} />,
    },
    {
      title: "Total Discounts",
      value: `${process.env.REACT_APP_SITE_CURRENCY}${summary.total_discount || 0}`,
      icon: <Discount sx={{ fontSize: 38, color: "#ed6c02" }} />,
    },
    {
      title: "Net Revenue",
      value: `${process.env.REACT_APP_SITE_CURRENCY}${summary.net_revenue || 0}`,
      icon: <AttachMoney sx={{ fontSize: 38, color: "#1976d2" }} />,
    },
  ];

  return (
    <div>
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        

        <Typography variant="h4" gutterBottom>
                Sales & Revenue Report
              </Typography>
      </div>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      
  
        {/* Filters */}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", paddingBottom: 3 }}>
          <TextField
            select
            label="Report Type"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            size="small"
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
          </TextField>

          <TextField
            label="Wash Type"
            value={washType}
            onChange={(e) => setWashType(e.target.value)}
            placeholder="Enter wash type (optional)"
            size="small"
          />

          <Button variant="contained" color="primary" onClick={fetchReport} startIcon={<SearchIcon />}>
            Search
          </Button>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              setWashType("");
              fetchReport();
            }}
          >
            Reset
          </Button>
        </Box>
      

      {/* Summary Cards */}
      {report?.summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {summaryCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ borderRadius: 2, boxShadow: 0, border: 1, borderColor: "#e0e0e0" }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {card.title}
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {card.value}
                      </Typography>
                    </Box>
                    {card.icon}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
  
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><b>Booking #</b></TableCell>
                <TableCell><b>Wash Type</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell><b>Revenue</b></TableCell>
                <TableCell><b>Refund</b></TableCell>
                <TableCell><b>Discount</b></TableCell>
                <TableCell><b>Booking Date</b></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {report?.listing?.rows?.length > 0 ? (
                report.listing.rows.map((row: any) => (
                  <TableRow key={row.booking_id}>
                    <TableCell>{row.booking_number}</TableCell>
                    <TableCell>{row.wash_types}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        color={
                          row.status === "completed"
                            ? "success"
                            : row.status === "cancelled"
                            ? "error"
                            : "default"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{process.env.REACT_APP_SITE_CURRENCY}{row.revenue}</TableCell>
                    <TableCell>{process.env.REACT_APP_SITE_CURRENCY}{row.refund_amount || 0}</TableCell>
                    <TableCell>{process.env.REACT_APP_SITE_CURRENCY}{row.discount_amount || 0}</TableCell>
                    <TableCell>
                      {new Date(row.booking_date).toLocaleDateString("en-IN")}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={report?.listing?.total_count || 0}
          page={page - 1}
          onPageChange={(e, newPage) => setPage(newPage + 1)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => {
            setLimit(parseInt(e.target.value, 10));
            setPage(1);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>
    </div>
  );
};

export default SalesRevenueReport;
